import { memo, useMemo } from 'react';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import { useNavigation, StackNavigationProp } from '@betty/navigation';
import { openDeposit } from '@betty/wallet/state';
import { selectWelcomePromos } from '@betty/promos/state';
import {
  BettyIgoLogo,
  Box,
  Button,
  Skeleton,
  useSafeAreaInsets,
} from '@betty/moonlight';
import { selectAccount } from '@betty/account';
import {
  getButtonState,
  handleVerifyAccount,
  handleVerifyIdentity,
} from './OnboardingHeader.utils';
import { ButtonState } from './OnboardingHeader.types';
import useOnfido from '../../hooks/useOnfido';
import BonusInfo from '../BonusInfo/BonusInfo';

function OnboardingHeader() {
  const { top } = useSafeAreaInsets();
  const dispatch = useDispatch();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const onfido = useOnfido();
  const promos = useSelector(selectWelcomePromos);
  const account = useSelector(selectAccount);
  const buttonState = useMemo(() => getButtonState(account), [account]);
  const actions = useMemo(
    () => ({
      [ButtonState.VERIFYING_ACCOUNT]: undefined,
      [ButtonState.PROVIDE_ID]: () => onfido.start(),
      [ButtonState.RETRY_VERIFICATION]: () => onfido.start(),
      [ButtonState.DEPOSIT]: () => dispatch(openDeposit()),
      [ButtonState.GET_UPDATE]: () => navigation.navigate('Account'),
      [ButtonState.CONTACT_SUPPORT]: () => navigation.navigate('Account'),
      [ButtonState.VERIFY_ACCOUNT]: () =>
        handleVerifyAccount(navigation, account),
      [ButtonState.VERIFY_IDENTITY]: () =>
        handleVerifyIdentity(navigation, account),
    }),
    [account, onfido, dispatch, navigation],
  );
  const { status } = account || {};

  return (
    <Box>
      <Box height={top} />
      <Box
        padding="space-400"
        flexDirection={{
          mobile: 'column',
          tablet: 'column',
          desktop: 'row',
        }}
        gap={{
          mobile: 'space-300',
          tablet: 'space-300',
          desktop: 'space-600',
        }}
        paddingTop={!top ? 'space-400' : 'space-0'}
      >
        <BettyIgoLogo />
        <BonusInfo
          flexGrow={1}
          promos={promos}
          textKey="yourWelcomeBonus"
          textNS="onboarding"
          progress={status === 'Approved' ? 98 : 95}
          gap={{
            mobile: promos.length === 1 ? 'space-400' : undefined,
            tablet: 'space-400',
            desktop: 'space-400',
          }}
          imageSize={64}
          horizontal
        />
        {buttonState ? (
          <Button
            locKey={`${buttonState}Button`}
            loading={onfido.initializing}
            disabled={buttonState === ButtonState.VERIFYING_ACCOUNT}
            alignSelf={{
              mobile: undefined,
              tablet: undefined,
              desktop: 'center',
            }}
            onPress={actions[buttonState]}
            locNS="onboarding"
          />
        ) : (
          <Skeleton
            height={40}
            alignSelf={{
              mobile: undefined,
              tablet: undefined,
              desktop: 'center',
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default memo(OnboardingHeader);
